import Close from '@mui/icons-material/Close';
import Box from '@mui/material/Box';
import Dialog from '@mui/material/Dialog';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { type KeyboardEvent, type ReactNode } from 'react';

import { ViewerIconButton } from './ViewerIconButton';
import {
  VIEWER_INK,
  VIEWER_INK_MUTED,
  type ViewerSurface,
  viewerSurfaceSx,
} from './viewerTokens';

type Props = {
  open: boolean;
  onClose: () => void;
  /** The dialog's accessible name. 'Delivery photos', 'Delivery videos'. */
  label: string;
  /** The counter line, left of the close button. '3 / 12'. */
  position?: string | undefined;
  /** Lets the owner claim the paging keys. Escape is the Dialog's. */
  onKeyDown?: ((event: KeyboardEvent) => void) | undefined;
  /** Which glass the content sits on. The reel wants the deeper one. */
  surface?: ViewerSurface | undefined;
  children: ReactNode;
};

/**
 * The room both viewers open in.
 *
 * A MUI `Dialog`, and that is the point of it. A full-screen viewer has to
 * trap focus, hide the page behind it from assistive technology, lock the
 * body's scroll, close on Escape and put focus back on the tile that was
 * clicked when it does — that is WCAG 2.4.3 and 2.1.2, and every one of those
 * is a line of someone else's tested code rather than one of ours. A
 * `position: fixed` div with a high `z-index` looks identical and does none of
 * it.
 *
 * `role="dialog"` and `aria-modal` come from the Dialog; the name is supplied
 * here, because "dialog" on its own tells a screen-reader user nothing about
 * what they have just been moved into.
 *
 * The chrome is the same in both viewers — a counter, a close button, a dark
 * surface — and is not worth writing twice. Everything below it differs
 * completely, which is why this takes children rather than trying to be both.
 */
export function ViewerShell({
  open,
  onClose,
  label,
  position,
  onKeyDown,
  surface = 'lightbox',
  children,
}: Props) {
  /**
   * Always a function, never `undefined` — and that is load-bearing.
   *
   * MUI composes an `onKeyDown` passed to `Dialog` with Modal's own handler,
   * which is the one that implements Escape. But it only composes a
   * *function*: hand it `undefined` and the undefined wins, Modal's handler is
   * replaced by nothing, and Escape silently stops closing the dialog. The
   * reel passes no paging keys of its own (it binds them to the scroll
   * container instead), so it was handing `Dialog` an `onKeyDown` of
   * `undefined` and had no working Escape key, while the lightbox beside it —
   * same component, same props but for this one — did.
   *
   * Wrapping it here means neither viewer can reintroduce that, and a future
   * third one cannot either.
   */
  const handleKeyDown = (event: KeyboardEvent) => onKeyDown?.(event);

  return (
    <Dialog
      fullScreen
      open={open}
      onClose={onClose}
      /*
        On the root, not on the paper.
        MUI focuses the element it traps focus in, which is the container
        *above* the paper — so a key pressed the instant the viewer opens
        never reaches a handler bound to a descendant of it, and the arrow
        keys silently did nothing until something inside had been clicked.
        The root sees every key pressed anywhere in the dialog, which is also
        where MUI's own Escape handling lives.
      */
      onKeyDown={handleKeyDown}
      slotProps={{
        /*
          Transparent, so the glass has the page to blur.

          `backdrop-filter` blurs whatever is painted *behind* the element, and
          what is behind a full-screen Dialog's paper is MUI's own backdrop —
          a flat 50%-black sheet. Left in place, the viewer would be frosting a
          grey rectangle, which looks identical to no blur at all. Clearing it
          puts the page itself behind the glass, which is the whole effect; the
          paper's own tint is what dims the page, so nothing is lost by
          removing the sheet that used to do it.
        */
        backdrop: { sx: { backgroundColor: 'transparent' } },
        paper: {
          /*
            The name has to be on the paper, because that is the element MUI
            gives `role="dialog"`. An `aria-label` on the `Dialog` is spread
            onto the root instead, where nothing is looking for it — which is
            how this viewer's predecessor ended up as an unnamed dialog.

            `aria-labelledby` is overridden to nothing for the same reason it
            has to be: MUI generates one unconditionally and points it at the
            id a `DialogTitle` would have had. There is no `DialogTitle` here,
            so that is a dangling reference — and a dangling `aria-labelledby`
            beats `aria-label`, leaving the dialog with no name at all rather
            than the wrong one. The paper's props are spread after MUI's, so
            these two win.
          */
          'aria-label': label,
          'aria-labelledby': undefined,
          sx: { ...viewerSurfaceSx(surface), color: VIEWER_INK, overflow: 'hidden' },
        },
      }}
    >
      {/*
        Over the content rather than above it. The stage behind is measured in
        `svh` and a bar in the flow would make "full height" mean two
        different things in the lightbox and the reel; floating it keeps the
        media centred in the whole screen in both.
      */}
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          zIndex: 2,
          px: { xs: 3, md: 5 },
          py: { xs: 3, md: 4 },
          // Clears the notch and the status bar on a phone in a PWA.
          pt: { xs: 'calc(env(safe-area-inset-top, 0px) + 12px)', md: 4 },
          gap: 4,
          pointerEvents: 'none',
          '& > *': { pointerEvents: 'auto' },
        }}
      >
        {position ? (
          <Typography
            // Announced, because a screen-reader user arrowing through the
            // stack has nothing else to tell them where they are.
            aria-live="polite"
            variant="body2"
            className="tabular-nums"
            sx={{ color: VIEWER_INK_MUTED, fontWeight: 600 }}
          >
            {position}
          </Typography>
        ) : (
          <Box />
        )}

        <ViewerIconButton label="Close" icon={<Close />} onClick={onClose} />
      </Stack>

      {children}
    </Dialog>
  );
}
